import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useUpdateProductGroup,
  useFuelProductGroupUpdate,
} from '@occc-app/dataAccessLayer/productGroup/mutations';
import { useGetProductGroupById } from '@occc-app/dataAccessLayer/productGroup/queries';
import { Product_Group_Key } from '@occc/graphql';
import { ConfirmDialog, useConfirmDialog, Typography } from '@occc-app/design-system';
import { useChainId } from '@occc-app/Chain/hooks';

import { UpdateProductGroupPage } from './UpdateProductGroupPage';

import { routeCreator, parseSortingRank } from '../utils';
import { ProductGroupFormValues } from '../types';

export const UpdateProductGroupPageContainer: React.FC = () => {
  const navigate = useNavigate();
  const chainId = useChainId();
  const { id: productGroupId = '' } = useParams<{ id: string }>();
  const [productGroupValues, setProductGroupValues] = React.useState<ProductGroupFormValues | null>(
    null,
  );
  const { loading, error, data: productGroup } = useGetProductGroupById(productGroupId, chainId);

  const onCompleted = () => {
    navigate(routeCreator.details(productGroupId));
  };

  const updateProductGroupConfig = {
    onCompleted,
    refetchQueries: ['productGroup'],
  };
  const [updateProductGroupMutation] = useUpdateProductGroup(updateProductGroupConfig);
  const [updateFuelProductGroup] = useFuelProductGroupUpdate(updateProductGroupConfig);

  const onUpdateDefaultTARProductGroup = () => {
    if (productGroupValues) {
      updateProductGroup(productGroupValues);
      setProductGroupValues(null);
    }
  };

  const onSubmit = (input: ProductGroupFormValues) => {
    if (
      input.default &&
      input.userSelectable &&
      productGroup?.fuelProductGroup?.default !== input.default
    ) {
      setProductGroupValues(input);
      setOpen(true);
      return;
    }

    updateProductGroup(input);
  };

  const { open, setOpen, onCloseButtonClick, onConfirmButtonClick } = useConfirmDialog({
    onConfirm: onUpdateDefaultTARProductGroup,
    shouldStopEventPropagation: true,
  });

  const updateProductGroup = (input: ProductGroupFormValues) => {
    const { userSelectable, default: isDefault, sortingRank, key, ...productGroupInput } = input;

    if (key === Product_Group_Key.FuelGradeId) {
      updateFuelProductGroup({
        variables: {
          productGroupId,
          input: {
            userSelectable,
            default: userSelectable ? isDefault : false,
            sortingRank: parseSortingRank(sortingRank) || 0,
            productGroup: productGroupInput,
          },
        },
      });
      return;
    }
    updateProductGroupMutation({
      variables: {
        id: productGroupId,
        input: productGroupInput,
      },
    });
  };

  return (
    <>
      <UpdateProductGroupPage
        loading={loading}
        error={error}
        productGroup={productGroup}
        onSubmit={onSubmit}
      />
      {open && (
        <ConfirmDialog
          dialogTitle='Default for Total Available Rollback'
          open={open}
          onClose={onCloseButtonClick}
          confirmButtonText='Confirm'
          confirmButtonProps={{
            onClick: onConfirmButtonClick,
          }}
        >
          <Typography variant='body2'>
            Are you sure you would like to set this product group as the default for all members
            when calculating Total Available Rollback? This will override your previous default.
          </Typography>
        </ConfirmDialog>
      )}
    </>
  );
};
